import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, AlertCircle, Clock, Ban } from "lucide-react";
import PlayfulNavbar from "../components/playful/PlayfulNavbar";
import PlayfulFooter from "../components/playful/PlayfulFooter";
import { CandyButton } from "../components/playful/CandyButton";
import { StickerCard } from "../components/playful/StickerCard";
import { GeometricBadge } from "../components/playful/GeometricBadge";
import { FloatingIcon } from "../components/playful/FloatingIcon";
import { Blob } from "../components/playful/Decor";
import { api, formatApiError } from "../lib/api";

export default function Unlock() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/preview/${encodeURIComponent(code)}`);
      setInfo(data);
      // If link is fully accessible (no password, not expired/exhausted/scheduled),
      // just go to the destination immediately.
      if (data.access_status === "ok" && data.url) {
        window.location.replace(data.url);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setInfo({ access_status: "not_found" });
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  const onUnlock = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setError("Enter the password.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post(`/preview/${encodeURIComponent(code)}/unlock`, {
        password,
      });
      if (data?.url) {
        window.location.replace(data.url);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setError("Incorrect password. Try again.");
      else if (status === 410) setError("This link is no longer available.");
      else if (status === 404) setError("Link not found or not yet active.");
      else setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const status = info?.access_status || "unknown";

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 font-mono text-[11px] uppercase tracking-[0.28em] text-neutral-700">
        LOADING…
      </div>
    );
  }

  // Password gate: render a stand-alone, unbranded modal — no navbar, no footer,
  // no decorations. Visitors arriving from a shared link shouldn't see LinkLane chrome.
  if (status === "password_required") {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-neutral-50 p-4"
        data-testid="unlock-page"
      >
        <form
          onSubmit={onUnlock}
          className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-8 shadow-lg md:p-10"
          data-testid="unlock-form-card"
        >
          <div className="mb-6 flex justify-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-neutral-200 bg-neutral-100">
              <Lock size={22} strokeWidth={2.25} className="text-neutral-700" />
            </div>
          </div>
          <h1
            className="text-center text-xl font-semibold text-neutral-900 md:text-2xl"
            data-testid="unlock-title"
          >
            Password required
          </h1>
          <p className="mt-2 text-center text-sm text-neutral-500">
            Enter the password to continue.
          </p>
          <div className="mt-6">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              placeholder="Password"
              data-testid="unlock-password-input"
              className="w-full rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-base text-neutral-900 placeholder-neutral-400 transition-colors focus:border-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-900"
            />
          </div>
          {error ? (
            <p className="mt-3 text-center text-sm text-red-600" data-testid="unlock-error-msg">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={submitting}
            data-testid="unlock-submit-btn"
            className="mt-5 w-full rounded-lg bg-neutral-900 py-2.5 text-base font-medium text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Unlocking…" : "Continue"}
          </button>
        </form>
      </div>
    );
  }

  const showError =
    status === "expired" ||
    status === "exhausted" ||
    status === "not_yet_active" ||
    status === "not_found";

  let errIcon = AlertCircle;
  let errTone = "pink";
  let errTitle = "Link unavailable.";
  let errBody = "This link can\u2019t be opened right now.";

  if (status === "expired") {
    errIcon = Ban;
    errTone = "pink";
    errTitle = "This link has expired.";
    errBody =
      "It was active for a limited time and is now closed. Ask whoever shared it for a fresh one.";
  } else if (status === "exhausted") {
    errIcon = Ban;
    errTone = "yellow";
    errTitle = "This link reached its click limit.";
    errBody = "It was a self-destructing link, and the click cap has been hit.";
  } else if (status === "not_yet_active") {
    errIcon = Clock;
    errTone = "mint";
    errTitle = "Not yet active.";
    errBody = info?.activates_at
      ? `This link unlocks on ${new Date(info.activates_at).toLocaleString()}.`
      : "This link is scheduled to unlock at a future time. Check back later.";
  } else if (status === "not_found") {
    errIcon = AlertCircle;
    errTone = "pink";
    errTitle = "Link not found.";
    errBody = "This short URL doesn\u2019t exist or has been deleted.";
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper text-ink" data-testid="unlock-page">
      <PlayfulNavbar />
      <section className="relative mx-auto max-w-3xl px-5 pb-12 pt-16 md:px-10 md:pt-24">
        <Blob
          tone="candy"
          size={260}
          className="absolute -right-10 -top-16 animate-float opacity-50"
        />
        <Blob tone="pink" size={200} className="absolute -bottom-10 -left-16 opacity-50" />
        <div className="relative">
          {showError ? (
            <StickerCard
              tone={errTone}
              bg="paper"
              hover={false}
              className="p-8 md:p-12"
              data-testid="unlock-error"
            >
              <FloatingIcon icon={errIcon} tone={errTone} size={68} rotate={-6} />
              <GeometricBadge tone={errTone} className="mt-4">
                {status.replace(/_/g, " ").toUpperCase()}
              </GeometricBadge>
              <h1
                className="mt-3 font-display text-4xl font-extrabold leading-tight md:text-5xl"
                data-testid="unlock-title"
              >
                {errTitle}
              </h1>
              <p className="mt-3 max-w-xl font-sans text-ink/80">{errBody}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <CandyButton variant="candy" withArrow onClick={() => navigate("/")}>
                  Back to LinkLane
                </CandyButton>
              </div>
            </StickerCard>
          ) : (
            <StickerCard tone="candy" bg="paper" hover={false} className="p-8 text-center">
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
                REDIRECTING…
              </span>
            </StickerCard>
          )}
        </div>
      </section>
      <PlayfulFooter />
    </div>
  );
}
